import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom-v5-compat'
import { toast } from 'react-toastify'
import { useSelector, shallowEqual } from 'react-redux'
import { useCSDetailsCachedData } from 'components/configured-service/useCSDetailsCachedData'
import { useCSDetailsURLData } from 'api/configured-service/useCSDetailsURLData'
import { useLazyFetchMetaFiltersQuery } from 'api/meta/getMetaFilters'
import { useGetTagsMetadataQuery } from 'api/tags/getTagsMetadata'
import { useUpdateConfiguredServiceMutation } from 'api/configured-service/configuredServiceCrud'
import { usePublishDraftConfiguredServiceMutation } from 'api/configured-service/draftPricingCSCrud'
import { selectIsRecurringService, selectIsDraftService } from 'data/configured-service/configuredServiceDetailsSelectors'
import { canUpdateService, canUpdateConfiguredServicePrice } from 'data/permissions/permissionsSelectors'
import { handleError } from 'utils/error'
import { getConfiguredServiceDetailsPageUrl } from 'router/routes'
import { useServiceDetailsFormDefaults } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormDefaults'
import { useServiceDetailsFormSubmission } from 'components/configured-service/accordions/ServiceDetailsAccordion/useServiceDetailsFormSubmission'
import { validateRecurrenceForPublish } from 'components/configured-service/EditServiceRecurrenceDialog/utils/validationHelpers'
import T from 'T'

const AdjustServiceContext = createContext(null)

export const AdjustServiceProvider = ({ serviceId, locationId: locationIdProp, accountId: accountIdProp, onPublishSuccess, children }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { locationId: urlLocationId, accountId: urlAccountId } = useCSDetailsURLData()
  const locationId = locationIdProp ?? urlLocationId
  const accountId = accountIdProp ?? urlAccountId
  const { data } = useCSDetailsCachedData({ id: serviceId })
  const canEdit = useSelector(canUpdateService, shallowEqual)
  const canEditPrice = useSelector(canUpdateConfiguredServicePrice, shallowEqual)
  const isRecurring = useMemo(() => selectIsRecurringService(data), [data])
  const isDraft = useMemo(() => selectIsDraftService(data), [data])

  const [isOpen, setIsOpen] = useState(() => searchParams.get('openAdjust') === 'true')
  const [recurrenceUpdated, setRecurrenceUpdated] = useState(false)

  const [fetchMetaFilters, { data: metaFiltersData }] = useLazyFetchMetaFiltersQuery()
  const [updateConfiguredService, { isLoading: isUpdating }] = useUpdateConfiguredServiceMutation()
  const [publishDraftServiceMutation, { isLoading: isPublishing }] = usePublishDraftConfiguredServiceMutation()

  useGetTagsMetadataQuery()

  useEffect(() => {
    if (searchParams.get('openAdjust') === 'true') {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          next.delete('openAdjust')
          return next
        },
        { replace: true }
      )
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    fetchMetaFilters({ body: {} })
  }, [fetchMetaFilters])

  const defaultFormValues = useServiceDetailsFormDefaults(data, metaFiltersData)
  const { formatFormData } = useServiceDetailsFormSubmission(serviceId)

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  })

  const {
    reset,
    handleSubmit,
    setError,
    control,
    formState: { isDirty },
  } = methods
  const hasUnsavedChanges = isDirty || recurrenceUpdated

  useEffect(() => {
    if (isOpen && !recurrenceUpdated) {
      reset(defaultFormValues)
    }
  }, [isOpen, defaultFormValues, reset, recurrenceUpdated])

  const watchedRecurrenceFields = useWatch({
    control,
    name: [
      'serviceType',
      'recurrence',
      'startDate',
      'recurrenceFrequency',
      'daysOfWeek',
      'eventInstanceType',
      'eventOccurrence',
      'dayOfTheWeek',
      'dayOfTheMonth',
    ],
  })

  const recurrencePublishCheck = useMemo(() => {
    const [
      serviceType,
      recurrence,
      startDate,
      recurrenceFrequency,
      daysOfWeek,
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
    ] = watchedRecurrenceFields || []
    return validateRecurrenceForPublish({
      serviceType,
      recurrence,
      startDate,
      recurrenceFrequency,
      daysOfWeek,
      eventInstanceType,
      eventOccurrence,
      dayOfTheWeek,
      dayOfTheMonth,
    })
  }, [watchedRecurrenceFields])
  const isRecurrencePublishable = recurrencePublishCheck.isValid
  const recurrencePublishBlockerMessage = recurrencePublishCheck.message
  const recurrencePublishBlockerReason = recurrencePublishCheck.reason || null

  const handleOpen = useCallback(() => {
    setRecurrenceUpdated(false)
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    reset(defaultFormValues)
  }, [reset, defaultFormValues])

  const handleToggle = useCallback(() => {
    if (isOpen) {
      handleClose()
    } else {
      handleOpen()
    }
  }, [isOpen, handleOpen, handleClose])

  const handleRecurrenceUpdated = useCallback(() => setRecurrenceUpdated(true), [])

  const handleFormError = useCallback(
    errorsList => {
      Object.keys(errorsList).forEach(key => {
        const error = errorsList[key]
        if (error) setError(key, error)
      })
    },
    [setError]
  )

  const handleSave = useCallback(
    async formData => {
      try {
        const formattedData = formatFormData(formData)
        await updateConfiguredService(formattedData).unwrap()
        setIsOpen(false)
        return true
      } catch (error) {
        handleError(error)
        return false
      }
    },
    [formatFormData, updateConfiguredService]
  )

  const handleSaveAndPublish = useCallback(
    async formData => {
      // fail closed before the mutation — RHF field-level rules miss
      // some incomplete-recurrence shapes (e.g. unmounted conditional fields).
      const recurrenceCheck = validateRecurrenceForPublish(formData)
      if (!recurrenceCheck.isValid) {
        toast.error(recurrenceCheck.message || T.FAILED_TO_PUBLISH_SERVICE)
        return
      }

      const saveAndPublish = async () => {
        const saveSuccess = await handleSave(formData)
        if (!saveSuccess) return null
        const publishedData = await publishDraftServiceMutation({ id: serviceId }).unwrap()
        if (onPublishSuccess) {
          onPublishSuccess(publishedData)
        } else {
          const baseUrl = getConfiguredServiceDetailsPageUrl(publishedData.configuredServiceId)
          const params = new URLSearchParams({ locationId, accountId }).toString()
          navigate(`${baseUrl}?${params}`)
        }
        return publishedData
      }

      toast.promise(saveAndPublish(), {
        pending: { render: () => T.SAVING_SERVICE, icon: true },
        success: { render: ({ data: publishResult }) => (publishResult ? T.SERVICE_PUBLISHED_SUCCESSFULLY : false), icon: true },
        error: { render: ({ data: errorData }) => errorData?.message || T.FAILED_TO_PUBLISH_SERVICE },
      })
    },
    [handleSave, publishDraftServiceMutation, serviceId, locationId, accountId, navigate, onPublishSuccess]
  )

  const onSaveClick = useCallback(() => {
    handleSubmit(handleSave, handleFormError)()
  }, [handleSubmit, handleSave, handleFormError])

  const onSaveAndPublishClick = useCallback(() => {
    handleSubmit(handleSaveAndPublish, handleFormError)()
  }, [handleSubmit, handleSaveAndPublish, handleFormError])

  const value = useMemo(
    () => ({
      isOpen,
      canEdit,
      isDraft,
      isRecurring,
      metaFiltersData,
      methods,
      isUpdating,
      isPublishing,
      canEditPrice,
      hasUnsavedChanges,
      isRecurrencePublishable,
      recurrencePublishBlockerMessage,
      recurrencePublishBlockerReason,
      handleOpen,
      handleClose,
      handleToggle,
      handleRecurrenceUpdated,
      onSaveClick,
      onSaveAndPublishClick,
    }),
    [
      isOpen,
      canEdit,
      isDraft,
      isRecurring,
      metaFiltersData,
      methods,
      isUpdating,
      isPublishing,
      canEditPrice,
      hasUnsavedChanges,
      isRecurrencePublishable,
      recurrencePublishBlockerMessage,
      recurrencePublishBlockerReason,
      handleOpen,
      handleClose,
      handleToggle,
      handleRecurrenceUpdated,
      onSaveClick,
      onSaveAndPublishClick,
    ]
  )

  return <AdjustServiceContext.Provider value={value}>{children}</AdjustServiceContext.Provider>
}

AdjustServiceProvider.propTypes = {
  serviceId: PropTypes.string.isRequired,
  locationId: PropTypes.string,
  accountId: PropTypes.string,
  onPublishSuccess: PropTypes.func,
  children: PropTypes.node.isRequired,
}

export const useAdjustService = () => {
  const ctx = useContext(AdjustServiceContext)
  if (!ctx) throw new Error('useAdjustService must be used within AdjustServiceProvider')
  return ctx
}
